/**
 * Original WebAudio RTS stingers for Empire Skirmish.
 * Classic-skirmish feel — not copyrighted Age of Empires assets.
 */

let ctx: AudioContext | null = null;
let muted = false;

const LS_MUTE = "x_empire_sfx_muted";

export function isEmpireMuted(): boolean {
  return muted;
}

export function setEmpireMuted(v: boolean) {
  muted = !!v;
  try {
    localStorage.setItem(LS_MUTE, muted ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function loadEmpireMute(): boolean {
  try {
    muted = localStorage.getItem(LS_MUTE) === "1";
  } catch {
    muted = false;
  }
  return muted;
}

function ac(): AudioContext | null {
  if (typeof window === "undefined" || muted) return null;
  try {
    ctx =
      ctx ||
      new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext)();
    if (ctx.state === "suspended") void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function beep(
  freq: number,
  dur: number,
  type: OscillatorType,
  gain = 0.08,
  when = 0
) {
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime + when;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.value = freq;
  g.gain.setValueAtTime(Math.max(0.0001, gain), t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  o.connect(g);
  g.connect(c.destination);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

function noiseBurst(dur: number, gain = 0.06, when = 0, hp = 400) {
  const c = ac();
  if (!c) return;
  const n = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, n, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = hp;
  const g = c.createGain();
  const t0 = c.currentTime + when;
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  src.connect(filter);
  filter.connect(g);
  g.connect(c.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

const lastPlay: Record<string, number> = {};
function throttle(key: string, ms: number): boolean {
  const now = performance.now();
  if ((lastPlay[key] || 0) + ms > now) return false;
  lastPlay[key] = now;
  return true;
}

/** Soft UI click on command buttons. */
export function playUiClick() {
  if (!throttle("ui", 40)) return;
  beep(720, 0.035, "triangle", 0.04);
  beep(980, 0.03, "sine", 0.025, 0.02);
}

/** Unit / building selected (short horn chirp). */
export function playSelect() {
  if (!throttle("select", 70)) return;
  beep(340, 0.06, "square", 0.045);
  beep(510, 0.08, "triangle", 0.055, 0.04);
  beep(680, 0.05, "sine", 0.03, 0.09);
}

function shout(phrase: string, pitch: number, rate: number) {
  if (muted || typeof window === "undefined" || !window.speechSynthesis) return;
  if (!throttle("shout", 240)) return;
  try {
    const synth = window.speechSynthesis;
    synth.cancel();
    const line = new SpeechSynthesisUtterance(phrase);
    line.pitch = pitch;
    line.rate = rate;
    line.volume = 1;
    const voice = synth
      .getVoices()
      .find((v) => /^en(-|_)/i.test(v.lang));
    if (voice) line.voice = voice;
    synth.speak(line);
  } catch {
    /* ignore */
  }
}

function pick(lines: string[]): string {
  return lines[Math.floor(Math.random() * lines.length)] || lines[0];
}

/** Villager selected. */
export function playVillagerShout() {
  shout("ohh yeah", 1.35, 1.05);
}

/** Warrior selected. */
export function playWarriorShout() {
  shout("mhmmm", 0.55, 0.78);
}

/** Building selected. */
export function playBuildingSelect() {
  shout("ready", 0.9, 0.95);
  if (!throttle("select", 70)) return;
  beep(340, 0.06, "square", 0.04);
  beep(510, 0.08, "triangle", 0.04, 0.04);
}

/** Move order. Villagers and warriors answer differently. */
export function playMoveAck(who: "villager" | "warrior" = "villager") {
  if (who === "warrior") shout(pick(["moving", "on the march", "mhmmm"]), 0.6, 0.82);
  else shout(pick(["okay", "on my way", "ohh yeah"]), 1.25, 1.05);
  if (!throttle("move", 90)) return;
  beep(who === "warrior" ? 280 : 420, 0.07, "triangle", 0.05);
  beep(who === "warrior" ? 360 : 560, 0.09, "triangle", 0.045, 0.06);
}

/** Gather order. The line follows the resource. */
export function playGatherOrder(kind?: string) {
  const line = kind === "wood"
    ? "chopping wood"
    : kind === "gold"
      ? "mining gold"
      : kind === "stone"
        ? "mining stone"
        : kind
          ? "gathering food"
          : "gathering";
  shout(line, 1.15, 1.02);
  if (!throttle("gatherOrd", 100)) return;
  if (kind === "wood") noiseBurst(0.06, 0.05, 0, 900);
  else if (kind === "gold" || kind === "stone") {
    beep(140, 0.05, "square", 0.04);
    noiseBurst(0.05, 0.04, 0.02, 400);
  } else noiseBurst(0.05, 0.03, 0, 700);
}

/** Work tick. Wood chops, stone knocks, food rustles. */
export function playGatherTick(kind?: string) {
  if (!throttle("gatherTick", 280)) return;
  if (kind === "wood") {
    noiseBurst(0.045, 0.05, 0, 1400);
    beep(160 + Math.random() * 30, 0.04, "square", 0.03, 0.01);
  } else if (kind === "gold" || kind === "stone") {
    beep(90 + Math.random() * 20, 0.06, "square", 0.045);
    noiseBurst(0.04, 0.04, 0.01, 350);
  } else {
    noiseBurst(0.04, 0.03, 0, 900);
    beep(220 + Math.random() * 40, 0.04, "triangle", 0.02, 0.01);
  }
}

/** Resource deposited at Town Hall. */
export function playDeposit() {
  if (!throttle("deposit", 180)) return;
  beep(520, 0.05, "sine", 0.05);
  beep(780, 0.07, "triangle", 0.045, 0.05);
}

/** Building placement / foundation laid. */
export function playBuildPlace() {
  shout(pick(["building", "I'll build that", "on it"]), 1.2, 1.0);
  if (!throttle("buildPlace", 120)) return;
  noiseBurst(0.08, 0.05, 0, 300);
  beep(220, 0.1, "square", 0.04, 0.02);
  beep(330, 0.12, "triangle", 0.045, 0.08);
}

/** Construction finished. */
export function playBuildComplete() {
  shout("finished", 1.1, 1);
  if (!throttle("buildDone", 150)) return;
  beep(440, 0.08, "triangle", 0.06);
  beep(554, 0.09, "triangle", 0.055, 0.07);
  beep(659, 0.12, "sine", 0.05, 0.14);
}

/** Train unit queued. */
export function playTrain() {
  shout("training", 0.95, 0.95);
  if (!throttle("train", 120)) return;
  beep(300, 0.06, "square", 0.04);
  beep(450, 0.08, "triangle", 0.05, 0.05);
  beep(600, 0.1, "sine", 0.045, 0.11);
}

/** Unit popped from building. */
export function playTrainDone() {
  shout("ready", 1.05, 1);
  if (!throttle("trainDone", 150)) return;
  beep(500, 0.07, "triangle", 0.055);
  beep(750, 0.1, "sine", 0.05, 0.06);
}

/** Attack order. */
export function playAttackOrder(who: "villager" | "warrior" = "warrior") {
  shout(who === "villager" ? "attack" : pick(["attack", "charge", "mhmmm"]), who === "villager" ? 1.15 : 0.5, 0.9);
  if (!throttle("atkOrd", 120)) return;
  beep(200, 0.1, "sawtooth", 0.05);
  beep(160, 0.12, "square", 0.04, 0.06);
  noiseBurst(0.06, 0.04, 0.04, 600);
}

/** Combat hit (throttled). */
export function playAttackHit() {
  if (!throttle("atkHit", 140)) return;
  noiseBurst(0.05, 0.055, 0, 500);
  beep(140 + Math.random() * 30, 0.07, "sawtooth", 0.04, 0.01);
}

/** Optional fog discovery ping — heavily throttled; never spam. */
export function playFogDiscover() {
  if (!throttle("fog", 2200)) return;
  beep(880, 0.04, "sine", 0.02);
  beep(1100, 0.05, "sine", 0.015, 0.04);
}

/** Chrono Vault picked up / charged. */
export function playChronoPickup() {
  if (!throttle("chronoPick", 200)) return;
  beep(660, 0.08, "sine", 0.05);
  beep(880, 0.1, "triangle", 0.055, 0.07);
  beep(1320, 0.14, "sine", 0.04, 0.14);
  beep(1760, 0.1, "sine", 0.025, 0.22);
}

/** Chronoshift fired (attacker). */
export function playChronoshift() {
  if (!throttle("chronoUse", 250)) return;
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = "sawtooth";
  o.frequency.setValueAtTime(120, t0);
  o.frequency.exponentialRampToValueAtTime(980, t0 + 0.35);
  o.frequency.exponentialRampToValueAtTime(60, t0 + 0.7);
  g.gain.setValueAtTime(0.001, t0);
  g.gain.exponentialRampToValueAtTime(0.12, t0 + 0.05);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.72);
  o.connect(g);
  g.connect(c.destination);
  o.start(t0);
  o.stop(t0 + 0.75);
  beep(440, 0.15, "sine", 0.05, 0.2);
  beep(220, 0.2, "triangle", 0.06, 0.4);
}

/** Chronoshift landed on you. */
export function playChronoVictim() {
  if (!throttle("chronoVic", 250)) return;
  beep(400, 0.12, "sawtooth", 0.06);
  beep(280, 0.15, "sawtooth", 0.05, 0.1);
  beep(160, 0.22, "square", 0.045, 0.22);
}

/** Victory fanfare. */
export function playVictory() {
  shout("we won", 1.15, 0.95);
  if (!throttle("win", 800)) return;
  const notes = [523, 659, 784, 1046];
  notes.forEach((f, i) => beep(f, 0.18, "triangle", 0.07, i * 0.12));
  beep(1318, 0.35, "sine", 0.05, 0.5);
}

/** Defeat thud. */
export function playDefeat() {
  shout("we lost", 0.7, 0.85);
  if (!throttle("lose", 800)) return;
  beep(220, 0.2, "sawtooth", 0.07);
  beep(165, 0.28, "square", 0.055, 0.15);
  beep(110, 0.4, "sine", 0.06, 0.35);
}

/** Draw — muted middle. */
export function playDraw() {
  if (!throttle("draw", 800)) return;
  beep(330, 0.15, "triangle", 0.05);
  beep(330, 0.2, "triangle", 0.04, 0.18);
}

/** Age-up fanfare (original synth). */
export function playAgeUp() {
  shout("advancing", 1.05, 0.92);
  if (!throttle("ageUp", 600)) return;
  const notes = [392, 494, 587, 784];
  notes.forEach((f, i) => beep(f, 0.14, "triangle", 0.06, i * 0.1));
  beep(988, 0.28, "sine", 0.045, 0.42);
}

export function playStop(who: "villager" | "warrior" = "villager") {
  shout("stopping", who === "warrior" ? 0.6 : 1.15, 0.95);
}

export function playHeal() {
  shout("healing", 1.3, 0.95);
  beep(660, 0.08, "sine", 0.04);
  beep(880, 0.1, "sine", 0.03, 0.06);
}

export function playResearch() {
  shout("researching", 0.95, 0.92);
  beep(520, 0.08, "triangle", 0.04);
  beep(680, 0.1, "sine", 0.03, 0.08);
}

export function playTrade() {
  shout("trading", 1.0, 0.95);
  beep(740, 0.05, "sine", 0.045);
  beep(980, 0.06, "sine", 0.03, 0.05);
}

/** Wall / stone place thud. */
export function playWallPlace() {
  shout("building", 1.05, 0.95);
  if (!throttle("wall", 100)) return;
  noiseBurst(0.1, 0.06, 0, 200);
  beep(140, 0.12, "square", 0.04, 0.02);
}

/**
 * War horn when an enemy steps onto your ground.
 * Original synth — brass fifth, answering call, war drums.
 * Returns false when muted or still inside the cooldown.
 */
export function playUnderAttack(): boolean {
  if (!throttle("war", 12000)) return false;
  shout("under attack", 0.7, 0.9);
  const c = ac();
  if (!c) return true;
  const horn = (freq: number, dur: number, when: number, gain: number) => {
    const t0 = c.currentTime + when;
    const o = c.createOscillator();
    const over = c.createOscillator();
    const filter = c.createBiquadFilter();
    const g = c.createGain();
    const overGain = c.createGain();
    o.type = "sawtooth";
    over.type = "triangle";
    o.frequency.setValueAtTime(freq * 1.06, t0);
    o.frequency.exponentialRampToValueAtTime(freq, t0 + Math.min(0.12, dur * 0.25));
    o.frequency.exponentialRampToValueAtTime(freq * 0.94, t0 + dur);
    over.frequency.setValueAtTime(freq * 2, t0);
    over.frequency.exponentialRampToValueAtTime(freq * 1.88, t0 + dur);
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(1400, t0);
    filter.frequency.exponentialRampToValueAtTime(480, t0 + dur);
    filter.Q.value = 0.8;
    overGain.gain.value = 0.28;
    g.gain.setValueAtTime(0.001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.045);
    g.gain.setValueAtTime(gain * 0.8, t0 + dur * 0.5);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(filter);
    over.connect(overGain);
    overGain.connect(filter);
    filter.connect(g);
    g.connect(c.destination);
    o.start(t0);
    over.start(t0);
    o.stop(t0 + dur + 0.03);
    over.stop(t0 + dur + 0.03);
  };
  const drum = (when: number, freq = 120) => {
    const t0 = c.currentTime + when;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(freq, t0);
    o.frequency.exponentialRampToValueAtTime(42, t0 + 0.2);
    g.gain.setValueAtTime(0.2, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.24);
    o.connect(g);
    g.connect(c.destination);
    o.start(t0);
    o.stop(t0 + 0.26);
    noiseBurst(0.07, 0.06, when, 160);
  };
  horn(349, 0.38, 0, 0.1);
  drum(0.02, 130);
  horn(262, 0.55, 0.16, 0.11);
  drum(0.36, 98);
  horn(349, 0.26, 0.7, 0.07);
  horn(220, 0.62, 0.86, 0.1);
  drum(0.88, 86);
  return true;
}
